/**
 * Faculty role identifiers used across the application
 */
export const FACULTY_ROLES = [
  "faculty",
  "teacher",
  "professor",
  "instructor",
  "admin",
];

/**
 * Check if a user has faculty role privileges
 * @param {string|Object} userRole - The user role (from getUserRole function) or user object
 * @param {Object} user - The user object (optional if userRole is already provided as string)
 * @returns {boolean} - True if user has faculty privileges
 */
export const isFacultyUser = (userRole, user = null) => {
  if (typeof userRole === "object" && userRole !== null) {
    user = userRole;
    userRole = null;
  }

  // Primary check: userRole from getUserRole function (if provided)
  if (typeof userRole === "string") {
    const roleString = userRole.toLowerCase();
    if (FACULTY_ROLES.includes(roleString)) {
      return true;
    }
  }

  // Check userType array first (for your session structure)
  if (Array.isArray(user?.userType)) {
    const hasFacultyType = user.userType.some(
      (type) => type.user_type?.toLowerCase() === "faculty"
    );
    if (hasFacultyType) {
      return true;
    }
  }

  // Secondary check: user.roles string inspection
  if (typeof user?.roles === "string") {
    const roleString = user.roles.toLowerCase();
    return FACULTY_ROLES.some((role) => roleString.includes(role));
  }

  // Tertiary check: user.roles array inspection
  if (Array.isArray(user?.roles)) {
    const userRoles = user.roles.map((role) => {
      const roleName =
        typeof role === "string"
          ? role
          : role.name || role.role || role.type || role.title || String(role);
      return roleName.toLowerCase();
    });

    return userRoles.some((role) => FACULTY_ROLES.includes(role));
  }

  return false;
};

/**
 * Check if user is librarian or admin
 * @param {Object} user - The user object
 * @returns {boolean} - True if user is librarian or admin
 */
export const isLibrarianOrAdmin = (user) => {
  if (!user) return false;

  // Check for specific flags
  if (user.isLibrarian || user.is_librarian || user.librarian) {
    return true;
  }

  // Check userType array for admin/librarian
  if (Array.isArray(user?.userType)) {
    const hasAdminLibrarianType = user.userType.some((type) => {
      const userType = type.user_type?.toLowerCase();
      return (
        userType === "librarian" ||
        userType === "admin" ||
        userType === "administrator"
      );
    });
    if (hasAdminLibrarianType) {
      return true;
    }
  }

  // Check roles string
  if (typeof user.roles === "string") {
    const roles = user.roles.toLowerCase();
    return roles.includes("librarian") || roles.includes("admin");
  }

  // Check roles array
  if (Array.isArray(user.roles)) {
    const userRoles = user.roles.map((role) => {
      const roleName =
        typeof role === "string"
          ? role
          : role.name || role.role || role.type || role.title || String(role);
      return roleName.toLowerCase();
    });

    return userRoles.includes("librarian") || userRoles.includes("admin");
  }

  return false;
};

/**
 * Check if user should see academic information fields
 * @param {string} userRole - The user role
 * @param {Object} user - The user object
 * @returns {boolean} - True if user should see academic info
 */
export const shouldShowAcademicInfo = (userRole, user) => {
  // Hide academic info for librarians, admins, and faculty
  return !isLibrarianOrAdmin(user) && !isFacultyUser(userRole, user);
};

/**
 * Get user title based on role
 * @param {string} userRole - The user role
 * @returns {string} - The display title for the user
 */
export const getUserTitle = (userRole) => {
  switch (userRole) {
    case "faculty":
      return "Faculty";
    case "librarian":
      return "Librarian";
    case "admin":
    case "administrator":
      return "Administrator";
    case "researcher":
      return "Researcher";
    case "visitor":
      return "Visitor";
    case "student":
      return "Student";
    case "patron":
      return "Patron";
    default:
      console.warn(`Unknown user role: ${userRole}, defaulting to Student`);
      return "Student";
  }
};
